import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy, Check, Share2, Users } from 'lucide-react';
import { api } from '@watchparty/shared';
import { Button } from '@/components/ui/Button';
import Avatar from '@/components/ui/Avatar';
// ---------------------------------------------------------------------------
// InviteControl
// ---------------------------------------------------------------------------
function InviteControl({ partyId, partyName }) {
    const [copied, setCopied] = useState(false);
    const { data: inviteData, isLoading } = useQuery({
        queryKey: ['party', partyId, 'invite'],
        queryFn: () => api.post(`/parties/${partyId}/members/invite`),
    });
    const inviteLink = inviteData?.code
        ? `${window.location.origin}/join/${inviteData.code}`
        : '';
    async function handleCopy() {
        await navigator.clipboard.writeText(inviteLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }
    async function handleShare() {
        try {
            await navigator.share({
                title: `Join ${partyName} on WatchParty`,
                text: "You've been invited to join a Watch Party!",
                url: inviteLink,
            });
        }
        catch {
            // User cancelled or share not supported
        }
    }
    if (isLoading || !inviteLink) {
        return (_jsx("div", { style: {
                height: '40px',
                background: 'var(--color-surface-sunken)',
                borderRadius: 'var(--radius-md)',
            } }));
    }
    return (_jsxs("div", { style: { display: 'flex', gap: '8px', alignItems: 'center' }, children: [_jsx("input", { readOnly: true, value: inviteLink, style: {
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-surface-sunken)',
                    color: 'var(--color-text-muted)',
                    fontSize: '0.8rem',
                    fontFamily: 'var(--font-family-body)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    outline: 'none',
                } }), _jsxs(Button, { variant: "secondary", size: "sm", onClick: handleCopy, style: { flexShrink: 0 }, children: [copied ? _jsx(Check, { size: 14 }) : _jsx(Copy, { size: 14 }), copied ? 'Copied!' : 'Copy'] }), typeof navigator !== 'undefined' && typeof navigator.share === 'function' && (_jsxs(Button, { variant: "secondary", size: "sm", onClick: handleShare, style: { flexShrink: 0 }, children: [_jsx(Share2, { size: 14 }), "Share"] }))] }));
}
// ---------------------------------------------------------------------------
// MembersTab
// ---------------------------------------------------------------------------
export function MembersTab({ partyId, party, isOwner, }) {
    const members = party.members ?? [];
    if (members.length === 0) {
        return (_jsxs("div", { style: { textAlign: 'center', padding: '64px 0' }, children: [_jsx(Users, { size: 36, style: { color: 'var(--color-text-faint)', marginBottom: '12px', opacity: 0.4 } }), _jsx("p", { style: {
                        fontFamily: 'var(--font-family-body)',
                        fontSize: '0.875rem',
                        color: 'var(--color-text-faint)',
                    }, children: "No members yet." })] }));
    }
    return (_jsxs("div", { children: [_jsxs("p", { style: {
                    fontFamily: 'var(--font-family-body)',
                    fontSize: '0.8rem',
                    color: 'var(--color-text-muted)',
                    margin: '0 0 16px',
                }, children: [members.length, " / ", party.maxMembers, " members"] }), _jsx("div", { children: members.map((member) => (_jsxs("div", { style: {
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 0',
                        borderBottom: '1px solid var(--color-border)',
                    }, children: [_jsx(Avatar, { displayName: member.displayName, avatarUrl: member.avatarUrl, avatarColor: member.avatarColor, size: 38 }), _jsxs("div", { style: { flex: 1 }, children: [_jsx("p", { style: {
                                        margin: 0,
                                        fontSize: '0.875rem',
                                        fontWeight: 700,
                                        color: 'var(--color-text)',
                                        fontFamily: 'var(--font-family-body)',
                                    }, children: member.displayName || 'Unknown' }), member.isOwner && (_jsx("p", { style: {
                                        margin: 0,
                                        fontSize: '0.75rem',
                                        color: 'var(--color-text-muted)',
                                        fontFamily: 'var(--font-family-body)',
                                    }, children: "Owner" }))] })] }, member.userId))) }), isOwner && (_jsxs("div", { style: {
                    marginTop: '24px',
                    padding: '16px',
                    background: 'var(--color-surface)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                }, children: [_jsx("p", { style: {
                            margin: '0 0 12px',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            color: 'var(--color-text)',
                            fontFamily: 'var(--font-family-body)',
                        }, children: "Invite to party" }), _jsx(InviteControl, { partyId: partyId, partyName: party.name })] }))] }));
}
